import {
  ReactNode,
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useRef,
  useState,
} from "react";

import { useElementHeight } from "../hooks/useElementHeight";

import styles from "./ResizablePanel.module.css";

interface ResizablePanelProps {
  children: ReactNode;
  /** The outer scroll container the panel sticks within. */
  scrollRef: RefObject<HTMLElement | null>;
  /** Height of scrollRef's visible area, when the parent already measures
      it. Omit to let the panel measure the element itself. */
  scrollerHeight?: number;
  /** Sticky offset below the toolbar. */
  offsetTop?: number;
  /** Optional ref to the panel's scroll element (e.g. for wheel forwarding). */
  panelScrollRef?: RefObject<HTMLDivElement | null>;
  /** Controlled width (px). Pair with onWidthChange so dragging still applies;
      omit to let the panel manage its own width starting at defaultWidth. */
  width?: number;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  /** aria-label root for the resizer ("Resize {label}"). */
  label?: string;
}

/**
 * A side panel with a draggable left-edge resizer and independent sticky
 * scroll. Renders as a fragment — a full-height 1px divider hosting the
 * resizer, then the sticky panel — so it drops into a flex-row layout beside
 * the content it docks against. Shared by the transcript and messages tabs so
 * the docked Search / Scans panel is identical on both.
 */
export const ResizablePanel = ({
  children,
  scrollRef,
  scrollerHeight: providedScrollerHeight,
  offsetTop = 0,
  panelScrollRef,
  width: controlledWidth,
  defaultWidth = 360,
  minWidth = 240,
  maxWidth = 800,
  onWidthChange,
  label,
}: ResizablePanelProps) => {
  const [internalWidth, setInternalWidth] = useState(defaultWidth);
  const width = controlledWidth ?? internalWidth;
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = { startX: e.clientX, startWidth: width };
    },
    [width]
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current) return;
      const { startX, startWidth } = dragRef.current;
      // Resizer is on the panel's left edge, so dragging left widens it.
      const next = startWidth - (e.clientX - startX);
      const clamped = Math.max(minWidth, Math.min(maxWidth, next));
      setInternalWidth(clamped);
      onWidthChange?.(clamped);
    },
    [minWidth, maxWidth, onWidthChange]
  );

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // pointer may already be released
    }
  }, []);

  // Cap the panel to the visible scroller height (100vh would include the app
  // navbar above the scroll container, leaving content unreachable).
  const measuredScrollerHeight = useElementHeight(
    scrollRef,
    providedScrollerHeight === undefined
  );
  const scrollerHeight = providedScrollerHeight ?? measuredScrollerHeight;

  const maxHeight =
    scrollerHeight > 0
      ? `${scrollerHeight - offsetTop}px`
      : `calc(100vh - ${offsetTop}px)`;

  return (
    <>
      {/* Full-height divider hosting the resizer, so the panel edge is
          grabbable anywhere along its height — not just beside the (often
          short) panel content. */}
      <div className={styles.divider}>
        <div
          className={styles.resizer}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${label ?? "panel"}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>
      <div
        ref={panelScrollRef}
        className={styles.panel}
        style={{ flex: `0 0 ${width}px`, top: offsetTop, maxHeight }}
      >
        {children}
      </div>
    </>
  );
};
